import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Wallet, Gift, Clock, User, Calendar, RefreshCw, AlertCircle } from "lucide-react";

const CreditsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [extendingId, setExtendingId] = useState(null);
  const [extendDays, setExtendDays] = useState("90");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.statut = filter;
      const res = await API.get("/admin/credits", { params });
      setCredits(res.data.credits || []);
      setStats(res.data.stats || {});
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCredits(); }, [filter]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await API.post("/admin/credits/sync");
      setSyncResult(res.data);
      toast.success(res.data.message);
      fetchCredits();
    } catch (error) {
      toast.error("Erreur synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const handleExtend = async (creditId) => {
    try {
      await API.put(`/admin/credits/${creditId}/extend`, { jours: parseInt(extendDays) || 90 });
      toast.success(`Crédit prolongé de ${extendDays} jours`);
      setExtendingId(null);
      fetchCredits();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const isExpired = (expireAt) => new Date(expireAt) < new Date();
  const daysLeft = (expireAt) => Math.max(0, Math.ceil((new Date(expireAt) - new Date()) / (1000 * 60 * 60 * 24)));

  return (
    <div className="space-y-6" data-testid="credits-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wallet className="h-7 w-7" />
            Gestion des crédits
          </h1>
        </div>
        <Button onClick={handleSync} disabled={syncing} variant="outline" data-testid="sync-credits-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Synchronisation..." : "Synchroniser les crédits"}
        </Button>
      </div>

      {/* Sync results */}
      {syncResult && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-4">
            <p className="font-medium mb-2">{syncResult.message}</p>
            <div className="space-y-1 text-sm">
              {syncResult.details?.map((d, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Badge className={d.status === "crédit créé" ? "bg-emerald-100 text-emerald-800" : d.status.includes("existe") ? "bg-gray-100 text-gray-600" : "bg-yellow-100 text-yellow-800"}>
                    {d.status}
                  </Badge>
                  <span>{d.client} — {d.titre}</span>
                  {d.montant > 0 && <span className="font-medium">{d.montant?.toFixed(2)} EUR</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Total généré</p>
            <p className="text-2xl font-bold text-emerald-600">{(stats.total_genere || 0).toFixed(2)} EUR</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Total utilisé</p>
            <p className="text-2xl font-bold text-blue-600">{(stats.total_utilise || 0).toFixed(2)} EUR</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Solde restant</p>
            <p className="text-2xl font-bold text-primary">{(stats.total_restant || 0).toFixed(2)} EUR</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Nb crédits</p>
            <p className="text-2xl font-bold">{stats.nb_credits || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
        {[
          { key: "all", label: "Tous" },
          { key: "actif", label: "Actifs" },
          { key: "epuise", label: "Épuisés" },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              filter === tab.key ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Credits list */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : credits.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Wallet className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="font-medium">Aucun crédit</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {credits.map((c) => (
            <Card key={c.id} className={c.statut === "actif" && !isExpired(c.expire_at) ? "border-emerald-200" : ""} data-testid={`credit-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{c.client_nom}</p>
                      <Badge className={
                        c.statut === "actif" && !isExpired(c.expire_at) ? "bg-emerald-100 text-emerald-800" :
                        c.statut === "epuise" ? "bg-gray-100 text-gray-600" :
                        "bg-red-100 text-red-800"
                      }>
                        {c.statut === "actif" && isExpired(c.expire_at) ? "Expiré" : c.statut === "actif" ? "Actif" : "Épuisé"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      <Gift className="h-3.5 w-3.5 inline mr-1" />
                      {c.livre_titre}
                    </p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      <span>Montant initial : <strong>{(c.montant_initial || 0).toFixed(2)} EUR</strong></span>
                      <span className={c.montant_restant > 0 ? "text-emerald-600 font-medium" : "text-muted-foreground"}>
                        Restant : {(c.montant_restant || 0).toFixed(2)} EUR
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{c.client_email}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Créé le {new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {isExpired(c.expire_at) ? (
                          <span className="text-red-600">Expiré</span>
                        ) : (
                          <span>Expire dans {daysLeft(c.expire_at)}j ({new Date(c.expire_at).toLocaleDateString("fr-FR")})</span>
                        )}
                      </span>
                      {c.created_by_nom && <span>Par {c.created_by_nom}</span>}
                    </div>

                    {/* Utilisations */}
                    {c.utilisations?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium">Utilisations :</p>
                        {c.utilisations.map((u, idx) => (
                          <p key={idx} className="text-xs text-muted-foreground pl-3">
                            -{(u.montant || 0).toFixed(2)} EUR le {new Date(u.date).toLocaleDateString("fr-FR")}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Admin actions */}
                  <div className="flex flex-col gap-2 items-end shrink-0">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-600">{(c.montant_restant || 0).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">EUR restant</p>
                    </div>
                    {isAdmin && extendingId === c.id ? (
                      <div className="flex gap-1 items-center">
                        <Input type="number" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className="w-16 h-8 text-sm" min="1" />
                        <span className="text-xs">j</span>
                        <Button size="sm" onClick={() => handleExtend(c.id)} className="h-8"><RefreshCw className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setExtendingId(null)} className="h-8 text-xs">x</Button>
                      </div>
                    ) : (
                      isAdmin && (isExpired(c.expire_at) || c.statut === "actif") && (
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => { setExtendingId(c.id); setExtendDays("90"); }}>
                          Prolonger
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreditsPage;
